import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import hrService from '../../../services/hrService';
import * as XLSX from 'xlsx';

const AttendanceUpload = ({ onUploadComplete }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e?.dataTransfer?.files);
    handleFileUpload(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files);
    handleFileUpload(files);
  };

  const handleFileUpload = async (files) => {
    if (files?.length === 0) return;

    const file = files?.[0];
    const allowedTypes = ['.csv', '.xlsx', '.xls'];
    const fileExtension = file?.name?.toLowerCase()?.substring(file?.name?.lastIndexOf('.'));

    if (!allowedTypes?.includes(fileExtension)) {
      setUploadStatus({
        type: 'error',
        message: 'Please upload a CSV or Excel file (.csv, .xlsx, .xls)'
      });
      return;
    }

    try {
      setIsProcessing(true);
      setUploadStatus({ type: 'uploading', message: 'Reading file...' });
      setUploadProgress(10);

      let fileContent;
      
      // Handle Excel files (.xlsx, .xls) by converting to CSV
      if (fileExtension === '.xlsx' || fileExtension === '.xls') {
        setUploadStatus({ type: 'uploading', message: 'Converting Excel file to CSV...' });
        fileContent = await readExcelAsCSV(file);
        setUploadProgress(25);
      } else {
        // Read CSV file directly
        fileContent = await readFileAsText(file);
      }
      setUploadProgress(30);

      setUploadStatus({ type: 'uploading', message: 'Auto-detecting CSV format and parsing data...' });

      // ENHANCED: Use auto-detection to handle both formats
      const parseResult = await hrService?.parseCSVAttendanceAuto(fileContent);
      setUploadProgress(50);

      // ENHANCED: Better error handling with format-specific messages
      if (!parseResult || !parseResult?.records) {
        throw new Error('Failed to parse CSV file. Please check the file format.');
      }

      if (parseResult?.records?.length === 0) {
        // ENHANCED: Provide format-specific error messages
        if (parseResult?.parseErrors?.length > 0) {
          const errorSummary = parseResult?.parseErrors?.slice(0, 3)?.map(e => e?.parseError)?.join('; ');
          throw new Error(`CSV parsing failed: ${errorSummary}${parseResult?.parseErrors?.length > 3 ? ' ...' : ''}`);
        } else {
          const formatHint = parseResult?.format === 'matrix' ?'\n1. Matrix format: Employees in rows, dates in columns (01-Nov to 30-Nov)\n2. First row must have employee info and date headers\n3. Attendance codes: P, A, WO, 1/2P, 1/4P, etc.' :'\n1. Standard format: One row per attendance record\n2. File has data rows (not just headers)\n3. Employee ID column is not empty\n4. File format matches the template';
          
          throw new Error(`No valid attendance records found in CSV file. Please check:${formatHint}`);
        }
      }

      // ENHANCED: Show format detection result
      const formatType = parseResult?.format === 'matrix' ? 'Matrix (Bulk Monthly)' : 'Standard (Daily)';
      setUploadStatus({ 
        type: 'uploading', 
        message: `Processing ${parseResult?.records?.length} attendance records from ${formatType} format...` 
      });
      setUploadProgress(70);

      // Upload records to database
      const uploadResult = await hrService?.uploadAttendanceRecords(parseResult?.records);
      setUploadProgress(100);

      // Success
      setUploadStatus({
        type: 'success',
        message: `Successfully processed ${file?.name} (${formatType} format). ${uploadResult?.successful} records imported${uploadResult?.failed > 0 ? `, ${uploadResult?.failed} failed` : ''}.`,
        details: {
          total: uploadResult?.totalRecords,
          success: uploadResult?.successful,
          failed: uploadResult?.failed,
          errors: uploadResult?.errors?.length || 0,
          parseErrors: parseResult?.parseErrors?.length || 0,
          format: formatType
        },
        uploadResult
      });

      // Notify parent component
      if (onUploadComplete) {
        onUploadComplete({
          fileName: file?.name,
          recordsProcessed: uploadResult?.successful,
          recordsFailed: uploadResult?.failed,
          uploadId: uploadResult?.uploadId,
          format: parseResult?.format
        });
      }

      // Reset file input
      if (fileInputRef?.current) {
        fileInputRef.current.value = '';
      }

    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus({
        type: 'error',
        message: error?.message || 'Failed to process file'
      });
      setUploadProgress(0);
    } finally {
      setIsProcessing(false);
    }
  };

  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  // Read Excel file and convert to CSV format
  const readExcelAsCSV = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          
          console.log('=== EXCEL FILE READING ===');
          console.log('File size:', data.length, 'bytes');
          
          // Try multiple reading strategies to handle different Excel date formats
          let workbook;
          let worksheet;
          let sheetName;
          
          // Strategy 1: Read with cellDates to get Date objects
          try {
            workbook = XLSX.read(data, { 
              type: 'array',
              cellDates: true,   // Parse dates as Date objects
              cellNF: true,      // Get number format
              cellText: true,    // Get text representation
              WTF: true          // Throw on error
            });
            
            sheetName = workbook.SheetNames[0];
            worksheet = workbook.Sheets[sheetName];
            
            console.log('Strategy 1 (cellDates=true) - Sheet name:', sheetName);
            console.log('Sheet ref:', worksheet['!ref']);
          } catch (err) {
            console.log('Strategy 1 failed:', err.message);
            
            // Strategy 2: Basic read
            workbook = XLSX.read(data, { type: 'array' });
            sheetName = workbook.SheetNames[0];
            worksheet = workbook.Sheets[sheetName];
          }
          
          // Get the range of the sheet
          const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
          console.log('Range:', range);
          console.log('Columns:', range.e.c - range.s.c + 1, 'Rows:', range.e.r - range.s.r + 1);
          
          // Debug: Log ALL first row cells with full details
          console.log('=== FIRST ROW CELLS (HEADER) - DETAILED ===');
          for (let C = range.s.c; C <= Math.min(range.e.c, 35); C++) {
            const cellRef = XLSX.utils.encode_cell({ r: 0, c: C });
            const cell = worksheet[cellRef];
            if (cell) {
              console.log(`Cell ${cellRef}:`, JSON.stringify({
                type: cell.t,
                value: cell.v,
                formatted: cell.w,
                format: cell.z,
                valueType: typeof cell.v
              }));
            } else {
              console.log(`Cell ${cellRef}: EMPTY/NULL`);
            }
          }
          
          // Process ALL cells in the worksheet to convert dates
          for (let R = range.s.r; R <= range.e.r; R++) {
            for (let C = range.s.c; C <= range.e.c; C++) {
              const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
              const cell = worksheet[cellRef];
              
              if (!cell) continue;
              
              // Priority 1: If cell.w (formatted value) exists, use it
              if (cell.w && typeof cell.w === 'string' && cell.w.trim()) {
                // Check if it looks like a date
                const w = cell.w.trim();
                if (/\d/.test(w) && (/[\/\-\.]/.test(w) || /jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec/i.test(w))) {
                  if (R === 0) console.log(`Using formatted value for ${cellRef}: "${w}"`);
                  cell.t = 's';
                  cell.v = w;
                }
              }
              
              // Priority 2: Handle JavaScript Date objects
              if (cell.v instanceof Date) {
                const date = cell.v;
                const day = date.getDate().toString().padStart(2, '0');
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const year = date.getFullYear();
                cell.t = 's';
                cell.v = `${day}/${month}/${year}`;
                cell.w = cell.v;
                if (R === 0) console.log(`Converted Date object ${cellRef}: ${cell.v}`);
              }
              
              // Priority 3: Handle Excel serial numbers (numbers between 40000-60000 are likely dates)
              if (cell.t === 'n' && typeof cell.v === 'number') {
                const num = cell.v;
                // Excel dates for 2020-2030 range are roughly 43831 to 47848
                if (num > 40000 && num < 60000) {
                  const excelEpoch = new Date(1899, 11, 30);
                  const jsDate = new Date(excelEpoch.getTime() + num * 24 * 60 * 60 * 1000);
                  const day = jsDate.getDate().toString().padStart(2, '0');
                  const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
                  const year = jsDate.getFullYear();
                  cell.t = 's';
                  cell.v = `${day}/${month}/${year}`;
                  cell.w = cell.v;
                  if (R === 0) console.log(`Converted Excel serial ${num} at ${cellRef}: ${cell.v}`);
                }
              }
            }
          }
          
          // Also try using sheet_to_json to see raw data
          console.log('=== RAW JSON OUTPUT (first 2 rows) ===');
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: true });
          console.log('Row 0 (header):', jsonData[0]);
          console.log('Row 1 (first data):', jsonData[1]);
          
          // Convert to CSV
          const csvContent = XLSX.utils.sheet_to_csv(worksheet, {
            blankrows: false,
            defval: ''
          });
          
          console.log('=== CSV OUTPUT ===');
          console.log('Excel converted to CSV (first 1500 chars):', csvContent.substring(0, 1500));
          const csvLines = csvContent.split('\n');
          console.log('Header row:', csvLines[0]);
          console.log('Second row:', csvLines[1]);
          console.log('Header columns count:', csvLines[0].split(',').length);
          
          // If CSV header has empty values but JSON has data, rebuild CSV from JSON
          if (csvLines[0].split(',').every(c => !c.trim()) && jsonData[0] && jsonData[0].length > 0) {
            console.log('=== REBUILDING CSV FROM JSON DATA ===');
            const rebuiltCSV = jsonData.map(row => {
              return (row || []).map(cell => {
                if (cell instanceof Date) {
                  const day = cell.getDate().toString().padStart(2, '0');
                  const month = (cell.getMonth() + 1).toString().padStart(2, '0');
                  const year = cell.getFullYear();
                  return `${day}/${month}/${year}`;
                }
                if (typeof cell === 'number' && cell > 40000 && cell < 60000) {
                  const excelEpoch = new Date(1899, 11, 30);
                  const jsDate = new Date(excelEpoch.getTime() + cell * 24 * 60 * 60 * 1000);
                  const day = jsDate.getDate().toString().padStart(2, '0');
                  const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
                  const year = jsDate.getFullYear();
                  return `${day}/${month}/${year}`;
                }
                // Escape commas and quotes in CSV
                const str = String(cell ?? '');
                if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                  return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
              }).join(',');
            }).join('\n');
            
            console.log('Rebuilt CSV header:', rebuiltCSV.split('\n')[0]);
            console.log('Rebuilt CSV row 1:', rebuiltCSV.split('\n')[1]);
            resolve(rebuiltCSV);
            return;
          }
          
          resolve(csvContent);
        } catch (error) {
          console.error('Error parsing Excel file:', error);
          reject(new Error('Failed to parse Excel file: ' + error.message));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read Excel file'));
      reader.readAsArrayBuffer(file);
    });
  };

  const downloadTemplate = () => {
    // ENHANCED: Provide both templates with clear column structure
    const standardCSV = "Employee ID,Employee Name,Date,Check In,Check Out,Status,Department,Notes\nEMP001,John Doe,2025-11-13,09:00,17:30,present,Engineering,\nEMP002,Jane Smith,2025-11-13,08:45,17:15,present,Marketing,";
    
    // FIXED: Matrix template with clear date headers and attendance codes ONLY in data rows
    // Header row shows dates, data rows show ONLY attendance codes (P, A, 1/2P, etc.)
    const matrixCSV = `Employee No,Employee Code,Employee Name,01-Dec,02-Dec,03-Dec,04-Dec,05-Dec,06-Dec,07-Dec,08-Dec,09-Dec,10-Dec,11-Dec,12-Dec,13-Dec,14-Dec,15-Dec,16-Dec,17-Dec,18-Dec,19-Dec,20-Dec,21-Dec,22-Dec,23-Dec,24-Dec,25-Dec,26-Dec,27-Dec,28-Dec,29-Dec,30-Dec,31-Dec
1,EMP001,John Doe,P,P,P,P,P,P,WO,WO,P,P,P,P,P,P,WO,WO,P,P,P,P,P,P,WO,WO,P,P,P,P,P,P,P
2,EMP002,Jane Smith,P,P,P,P,A,P,WO,WO,P,P,P,1/2,P,P,WO,WO,P,P,P,P,P,P,WO,WO,P,P,P,P,P,P,P
3,EMP003,Robert Johnson,P,P,P,P,P,P,WO,WO,P,P,P,P,P,P,WO,WO,P,P,P,P,A,P,WO,WO,P,P,P,P,P,P,P
4,EMP004,Emily Davis,P,P,P,P,P,P,WO,WO,P,P,1/2,P,P,P,WO,WO,P,P,P,P,P,P,WO,WO,P,P,P,P,P,P,P
5,EMP005,Michael Wilson,P,P,P,A,P,P,WO,WO,P,P,P,P,P,P,WO,WO,P,P,P,P,P,P,WO,WO,P,P,P,P,P,P,P
6,EMP006,Sarah Brown,P,P,P,P,P,P,WO,WO,P,P,P,P,P,1/2,WO,WO,P,P,P,P,P,P,WO,WO,P,P,P,P,P,P,P
7,EMP007,David Martinez,P,P,P,P,P,P,WO,WO,P,P,P,P,P,P,WO,WO,P,A,P,P,P,P,WO,WO,P,P,P,P,P,P,P
8,EMP008,Lisa Anderson,P,P,P,P,P,P,WO,WO,P,P,P,P,P,P,WO,WO,P,P,P,P,P,P,WO,WO,P,P,1/2,P,P,P,P
9,EMP009,James Taylor,P,1/2,P,P,P,P,WO,WO,P,P,P,P,P,P,WO,WO,P,P,P,P,P,P,WO,WO,P,P,P,P,P,P,P
10,EMP010,Jennifer Thomas,P,P,P,P,P,A,WO,WO,P,P,P,P,P,P,WO,WO,P,P,P,P,P,P,WO,WO,P,P,P,P,P,P,P`;
    
    // Ask user which format they want
    const useMatrix = window.confirm(
      'Choose CSV Template Format:\n\n' + 'OK = Matrix Format (Employees in rows, dates in columns)\n'+ '     • Date columns show ONLY attendance codes (P, A, 1/2P, WO)\n'+ '     • Header row shows dates for reference\n\n'+ 'Cancel = Standard Format (One row per attendance record - for daily imports)'
    );
    
    const csvContent = useMatrix ? matrixCSV : standardCSV;
    const fileName = useMatrix ? 'attendance_matrix_template.csv' : 'attendance_template.csv';
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  const resetUpload = () => {
    setUploadStatus(null);
    setUploadProgress(0);
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Bulk Attendance Upload</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Upload attendance data in CSV or Excel format (.csv, .xlsx, .xls)
          </p>
        </div>
        <Button
          variant="outline"
          onClick={downloadTemplate}
          iconName="Download"
          iconPosition="left"
        >
          Download Template
        </Button>
      </div>
      {/* Upload Area */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-smooth cursor-pointer
          ${isDragOver 
            ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
          }
          ${isProcessing ? 'pointer-events-none opacity-50' : ''}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !isProcessing && fileInputRef?.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileSelect}
          className="hidden"
          disabled={isProcessing}
        />

        <div className="flex flex-col items-center space-y-4">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
            {isProcessing ? (
              <div className="animate-spin">
                <Icon name="Loader2" size={32} color="var(--color-primary)" />
              </div>
            ) : (
              <Icon name="Upload" size={32} color="var(--color-primary)" />
            )}
          </div>
          
          <div>
            <p className="text-lg font-medium text-foreground">
              {isProcessing ? 'Processing...' : 'Drop your file here or click to browse'}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Supports CSV and Excel files up to 10MB
            </p>
          </div>

          <div className="flex items-center space-x-4 text-xs text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Icon name="FileText" size={16} />
              <span>CSV</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="FileSpreadsheet" size={16} />
              <span>Excel (.xlsx, .xls)</span>
            </div>
          </div>
        </div>
      </div>
      {/* Upload Status */}
      {uploadStatus && (
        <div className="mt-6">
          {uploadStatus?.type === 'uploading' && (
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="animate-spin">
                  <Icon name="Loader2" size={20} color="var(--color-primary)" />
                </div>
                <span className="text-sm font-medium text-foreground">
                  {uploadStatus?.message}
                </span>
              </div>
              <div className="w-full bg-border rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-smooth"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {uploadProgress}% complete
              </p>
            </div>
          )}

          {uploadStatus?.type === 'success' && (
            <div className="bg-success/10 border border-success/20 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-success">
                    Upload Successful
                  </p>
                  <p className="text-sm text-foreground mt-1">
                    {uploadStatus?.message}
                  </p>
                  {uploadStatus?.details && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-xs">
                      <div>
                        <span className="text-muted-foreground">Total:</span>
                        <span className="ml-1 font-medium">{uploadStatus?.details?.total}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Success:</span>
                        <span className="ml-1 font-medium text-success">{uploadStatus?.details?.success}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Failed:</span>
                        <span className="ml-1 font-medium text-error">{uploadStatus?.details?.failed}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Parse Errors:</span>
                        <span className="ml-1 font-medium text-warning">{uploadStatus?.details?.parseErrors}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Show errors if any */}
                  {uploadStatus?.uploadResult?.errors?.length > 0 && (
                    <div className="mt-4 p-3 bg-error/5 border border-error/10 rounded">
                      <p className="text-xs font-medium text-error mb-2">Upload Errors:</p>
                      <div className="text-xs text-muted-foreground max-h-32 overflow-y-auto">
                        {uploadStatus?.uploadResult?.errors?.slice(0, 5)?.map((error, index) => (
                          <p key={index} className="mb-1">
                            Row {error?.row}: {error?.error}
                          </p>
                        ))}
                        {uploadStatus?.uploadResult?.errors?.length > 5 && (
                          <p className="text-warning">... and {uploadStatus?.uploadResult?.errors?.length - 5} more errors</p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={resetUpload}
                    >
                      Upload Another File
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {uploadStatus?.type === 'error' && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-error">Upload Failed</p>
                  <p className="text-sm text-foreground mt-1">{uploadStatus?.message}</p>
                  
                  <div className="mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={resetUpload}
                    >
                      Try Again
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Upload Guidelines */}
      <div className="mt-6 bg-muted/50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-foreground mb-3">Upload Guidelines:</h4>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li><strong className="text-foreground">Supported Formats:</strong> CSV (.csv) and Excel (.xlsx, .xls) - Excel files are auto-converted to CSV</li>
          <li><strong className="text-foreground">Format Auto-Detection:</strong> System automatically detects data format</li>
          <li>• <strong>Standard Format:</strong> Employee ID, Date (required) | Check In/Out, Status, Notes (optional)</li>
          <li>• <strong>Matrix Format:</strong> Employees in rows, dates (01-30) in columns with codes</li>
          <li>• <strong>Attendance codes (Matrix):</strong> P (Present), A (Absent), WO (Week Off), 1/2P (Half Day), 1/4P (Quarter Day), L (Late), ED (Early Departure)</li>
          <li>• <strong>Time format (Standard):</strong> Use 24-hour format (e.g., 09:00, 17:30)</li>
          <li>• <strong>Date format (Standard):</strong> YYYY-MM-DD or MM/DD/YYYY</li>
          <li>• <strong>Status values (Standard):</strong> present, absent, late, early_departure, on_leave</li>
          <li>• <strong>Employee IDs:</strong> Must match existing employee records in the system</li>
          <li>• <strong>Maximum file size:</strong> 10MB</li>
          <li>• <strong>Duplicates:</strong> Records for the same employee and date will be updated</li>
        </ul>
      </div>
    </div>
  );
};

export default AttendanceUpload;